import Box from '@mui/material/Box';
import AsyncAutocomplete from 'pages/Utilities/EpicAdministration/components/AsyncAutocomplete';
import Button from '@mui/material/Button';
import { useState } from 'react';
import { DEFAULT_LIST_ITEM } from 'pages/Utilities/EpicAdministration/_const';
import CircularProgressWithLabel from 'pages/Utilities/EpicAdministration/components/CircularProgressWithLabel';
import styles from '../IMSLDAPValidator.module.scss';
import { GetLDAP } from '../IMSLDAPValidator_api';
import IMSResults from './IMSResults';
import InfonetResults from './InfonetResults';
import HRData from './HRData';
import PeoplesoftResults from './PeoplesoftResults';
import Typography from '@mui/material/Typography';
import { InfoDialog } from '../components/InfoDialog';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import PeoplesoftOnPremResults from './PeoplesoftOnPremResults';
import Grid from '@mui/material/Grid';

const IMSLDAPValidatorSearch = () => {
	const [sSearchValue, setSearchValue] = useState('');
	const [sIsSearching, setIsSearching] = useState(false);
	const [sIMSResults, setIMSResults] = useState([]);
	const [sHRData, setHRData] = useState([]);
	const [sInfonetResults, setInfonetResults] = useState([]);
	const [sPeoplesoftResults, setPeoplesoftResults] = useState([]);
	const [peoplesoftOnPremResults, setPeoplesoftOnPremResults] = useState([]);
	const [sIsResultsExpanded, setIsResultsExpanded] = useState(false);
	const [error, setError] = useState('');
	const [sIsDialogOpen, setIsDialogOpen] = useState(false);

	const search = async () => {
		try {
			setError('');
			setIsResultsExpanded(false);
			if (!sSearchValue || sSearchValue.length === 0)
				return setError('No ESO or Empoyee ID was given');

			setIsSearching(true);
			const ldapResults = await GetLDAP(sSearchValue);
			const ldapData = ldapResults.data.payload;

			const AccountStatusType = {
				0: 'Yes',
				1: 'No',
				2: 'Good',
				3: 'Bad',
				4: 'Error',
			};
			if (ldapData.imsInfo.ErrorMessage) {
				setIsSearching(false);
				return setError('No user with this ID exists.');
			}
			const accountStatusInfo = ldapData.accountStatusInfo;
			const convertedAccountStatusInfo = {
				HRDataInIMS: AccountStatusType[accountStatusInfo.HRDataInIMS],
				InfnetEntryExists:
					AccountStatusType[accountStatusInfo.InfnetEntryExists],
				MyHubOIDEntryExists:
					AccountStatusType[accountStatusInfo.MyHubOIDEntryExists],
				OracleSecondaryUIDAttribute:
					AccountStatusType[
						accountStatusInfo.OracleSecondaryUIDAttribute
					],
				PSFTEntryExists:
					AccountStatusType[accountStatusInfo.PSFTEntryExists],
				RegisteredInMyHub:
					AccountStatusType[accountStatusInfo.RegisteredInMyHub],
			};
			setHRData(
				Object.keys(convertedAccountStatusInfo).length > 0
					? convertedAccountStatusInfo
					: [{ ...DEFAULT_LIST_ITEM }],
			);
			setIMSResults(
				Object.keys(ldapData.imsInfo).length > 0
					? ldapData.imsInfo
					: [{ ...DEFAULT_LIST_ITEM }],
			);
			setInfonetResults(
				Object.keys(ldapData.infonetUser).length > 0
					? ldapData.infonetUser
					: [{ ...DEFAULT_LIST_ITEM }],
			);
			setPeoplesoftResults(
				Object.keys(ldapData.peoplesoftUser).length > 0
					? ldapData.peoplesoftUser
					: [{ ...DEFAULT_LIST_ITEM }],
			);
			setPeoplesoftOnPremResults(
				Object.keys(ldapData.peoplesoftOnPremUser).length > 0
					? ldapData.peoplesoftOnPremUser
					: [{ ...DEFAULT_LIST_ITEM }],
			);
			setIsResultsExpanded(true);
			setIsSearching(false);
		} catch (ex) {
			setError(ex.message);
		}
	};

	return (
		<Box>
			<div className={styles.searchRow}>
				<InfoDialog
					icon={<QuestionMarkIcon color='secondary' />}
					open={sIsDialogOpen}
					onClose={() => setIsDialogOpen(false)}
				/>
				<Button
					variant={'text'}
					onClick={() => setIsDialogOpen(isOpen => !isOpen)}
				>
					HELP
				</Button>
				<AsyncAutocomplete
					className={styles.searchBox}
					archival={false}
					label='IMS LDAP Validator Search'
					placeholder='Enter NTID,ESO, or Employee ID'
					handleChange={value => {
						setSearchValue(value?.label ?? value);
					}}
					handleKeystroke={value => {
						setSearchValue(value);
					}}
					value={sSearchValue}
					sx={{ flexGrow: 1 }}
					freeSolo
				/>
				<Button variant={'outlined'} onClick={search}>
					Search
				</Button>
			</div>
			<div>
				{error && (
					<Typography color='error' className={styles.searchSpinner}>
						{error}
					</Typography>
				)}
				{sIsSearching ? (
					<div className={styles.searchSpinner}>
						<CircularProgressWithLabel />
					</div>
				) : (
					sIsResultsExpanded && (
						<Grid
							container
							sx={{
								p: 3,
								display: 'grid',
								placeItems: 'center',
								justifyContent: 'center',
							}}
						>
							<Grid item xs={12} sx={{ width: '100%', m: 2 }}>
								<IMSResults dataResults={sIMSResults} />
							</Grid>
							<Grid item xs={12}>
								<HRData dataResults={sHRData} />
							</Grid>
							<Grid
								xs={12}
								container
								spacing={1}
								sx={{
									display: 'flex',
									justifyContent: 'space-evenly',
								}}
							>
								<Grid
									item
									xs={12}
									sm={6}
									md={4}
									sx={{
										display: 'flex',
										justifyContent: 'center',
									}}
								>
									<PeoplesoftResults
										dataResults={sPeoplesoftResults}
									/>
								</Grid>
								<Grid
									item
									xs={12}
									sm={6}
									md={4}
									sx={{
										display: 'flex',
										justifyContent: 'center',
									}}
								>
									<InfonetResults
										dataResults={sInfonetResults}
									/>
								</Grid>
								<Grid
									item
									xs={12}
									sm={6}
									md={4}
									sx={{
										display: 'flex',
										justifyContent: 'center',
									}}
								>
									<PeoplesoftOnPremResults
										dataResults={peoplesoftOnPremResults}
									/>
								</Grid>
							</Grid>
						</Grid>
					)
				)}
			</div>
		</Box>
	);
};

export default IMSLDAPValidatorSearch;
